import { useState, useEffect } from "react";
import { useSearchParams } from "react-router";
import { AlertTriangle, CheckCircle, Loader2, XCircle } from "lucide-react";
import publicApi from "../../services/publicApi";

/**
 * Página PÚBLICA do comprador para cancelar a renovação da própria assinatura.
 * Chega pelo link do e-mail de aviso de renovação, autorizada por token assinado.
 *
 * O cancelamento acontece só no POST, após confirmação explícita: cliente de e-mail e
 * scanner de segurança fazem prefetch de link, e cancelar ao abrir a página cancelaria
 * assinatura sem ninguém clicar.
 */

interface CancelInfo {
    subscriptionCode: string;
    status: string;
    jaCancelada: boolean;
    productName: string | null;
    sellerName: string | null;
    nextCycleDate: string | null;
    amount: string | number | null;
    buyerName: string | null;
}

const formatarData = (valor?: string | null) => {
    if (!valor) return "—";
    const d = new Date(valor);
    if (Number.isNaN(d.getTime())) return "—";
    // UTC: nextCycleDate é um campo de data, sem hora — o fuso local deslocaria o dia
    return d.toLocaleDateString("pt-BR", { timeZone: "UTC" });
};

const formatarValor = (valor?: string | number | null) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(valor ?? 0));

export default function CancelRenewal() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");

    const [carregando, setCarregando] = useState(true);
    const [info, setInfo] = useState<CancelInfo | null>(null);
    const [erro, setErro] = useState<string | null>(null);
    const [enviando, setEnviando] = useState(false);
    const [cancelada, setCancelada] = useState(false);

    useEffect(() => {
        const carregar = async () => {
            if (!token) {
                setErro("Link inválido. Abra o link direto do e-mail que você recebeu.");
                setCarregando(false);
                return;
            }

            try {
                const { data } = await publicApi.get("/api/public/subscriptions/cancel-info", {
                    params: { token },
                });
                setInfo(data);
                if (data?.jaCancelada) setCancelada(true);
            } catch (e: any) {
                setErro(
                    e?.response?.data?.error ||
                    "Não foi possível carregar os dados da sua assinatura. Tente novamente mais tarde."
                );
            } finally {
                setCarregando(false);
            }
        };

        carregar();
    }, [token]);

    const confirmarCancelamento = async () => {
        if (!token) return;
        setEnviando(true);
        setErro(null);

        try {
            await publicApi.post("/api/public/subscriptions/cancel", { token });
            setCancelada(true);
        } catch (e: any) {
            setErro(
                e?.response?.data?.error ||
                "Não foi possível cancelar agora. Tente novamente em alguns minutos."
            );
        } finally {
            setEnviando(false);
        }
    };

    const Moldura = ({ children }: { children: React.ReactNode }) => (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-10 dark:bg-gray-900">
            <div className="w-full max-w-lg rounded-2xl border border-gray-200 bg-white p-7 shadow-sm dark:border-gray-800 dark:bg-gray-800">
                {children}
            </div>
        </div>
    );

    if (carregando) {
        return (
            <Moldura>
                <div className="flex flex-col items-center gap-3 py-8">
                    <Loader2 className="h-7 w-7 animate-spin text-brand-500" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">Carregando sua assinatura…</p>
                </div>
            </Moldura>
        );
    }

    if (erro && !info) {
        return (
            <Moldura>
                <div className="flex flex-col items-center gap-3 text-center">
                    <XCircle className="h-11 w-11 text-red-500" />
                    <h1 className="text-xl font-semibold text-gray-800 dark:text-white/90">Não foi possível continuar</h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{erro}</p>
                </div>
            </Moldura>
        );
    }

    if (cancelada) {
        return (
            <Moldura>
                <div className="flex flex-col items-center gap-3 text-center">
                    <CheckCircle className="h-11 w-11 text-green-500" />
                    <h1 className="text-xl font-semibold text-gray-800 dark:text-white/90">
                        Renovação automática cancelada
                    </h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Você não será cobrado novamente
                        {info?.productName ? <> por <strong>{info.productName}</strong></> : null}.
                        {info?.nextCycleDate ? (
                            <> Seu acesso continua até <strong>{formatarData(info.nextCycleDate)}</strong>.</>
                        ) : null}
                    </p>
                    {info?.subscriptionCode ? (
                        <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                            Assinatura {info.subscriptionCode}
                        </p>
                    ) : null}
                </div>
            </Moldura>
        );
    }

    return (
        <Moldura>
            <div className="flex flex-col gap-5">
                <div className="flex flex-col items-center gap-2 text-center">
                    <AlertTriangle className="h-10 w-10 text-amber-500" />
                    <h1 className="text-xl font-semibold text-gray-800 dark:text-white/90">
                        Cancelar a renovação automática?
                    </h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        {info?.buyerName ? `${info.buyerName}, c` : "C"}onfira os dados antes de confirmar.
                        Você não será cobrado de novo, e o acesso continua até o fim do período já pago.
                    </p>
                </div>

                <dl className="divide-y divide-gray-100 rounded-xl bg-gray-50 px-4 dark:divide-gray-700 dark:bg-gray-900/40">
                    {[
                        ["Produto", info?.productName || "—"],
                        ["Vendido por", info?.sellerName || "—"],
                        ["Renovaria em", formatarData(info?.nextCycleDate)],
                        ["Valor da renovação", formatarValor(info?.amount)],
                    ].map(([rotulo, valor]) => (
                        <div key={rotulo as string} className="flex items-center justify-between gap-4 py-3">
                            <dt className="text-sm text-gray-500 dark:text-gray-400">{rotulo}</dt>
                            <dd className="text-right text-sm font-medium text-gray-800 dark:text-white/90">{valor}</dd>
                        </div>
                    ))}
                </dl>

                {erro ? (
                    <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
                        {erro}
                    </p>
                ) : null}

                <button
                    type="button"
                    onClick={confirmarCancelamento}
                    disabled={enviando}
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-3 text-base font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                    {enviando ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                    {enviando ? "Cancelando…" : "Sim, cancelar a renovação"}
                </button>

                <p className="text-center text-xs text-gray-400 dark:text-gray-500">
                    Se você fechar esta página sem confirmar, nada muda e a renovação segue normalmente.
                </p>
            </div>
        </Moldura>
    );
}
